import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  Platform,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import Icon from '/component/base/Icon';
import KeyboardInput from '/component/base/KeyboardInput';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {formatUSPhone, isValidUSPhone} from '../../util/manageMySite.util';

import {styles} from './PhoneInput.style';
import type {PhoneInputProps} from './PhoneInput.types';

const {phoneInput} = ManageMySiteTestIDs;
const PHONE_PLACE_HOLDER = String(tt('phoneNumber', 'Enter phone number'));

const normalizeDigits = (value: string = ''): string =>
  String(value).replace(/\D/g, '');

const normalizeUSPhoneForSave = (value: string = ''): string => {
  const digits = normalizeDigits(value);

  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }

  return digits;
};

export const PhoneInput = forwardRef<any, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      onBlurValue,
      hasError = false,
      errorText = '',
      onDelete,
      accessibilityLabel = '',
      accessibilityHint,
      accessibilityValue,
      accessibilityState,
      country = 'US',
      hideDeleteFromAccessibility = false,
      testID = '',
    },
    ref,
  ) => {
    const inputRef = useRef<TextInput | null>(null);
    const focusableRef = useRef<any>(null);

    const [localValue, setLocalValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const isUS = country === 'US';

    const resolvedAccessibilityLabel = accessibilityLabel || 'Phone number';
    const resolvedAccessibilityHint =
      accessibilityHint || 'Double tap to edit phone number';

    const focusInputAccessibility = () => {
      const node = findNodeHandle(inputRef.current);

      if (!node) {
        return;
      }

      setTimeout(
        () => {
          AccessibilityInfo.setAccessibilityFocus(node);
        },
        Platform.OS === 'android' ? 180 : 80,
      );
    };

    useImperativeHandle(ref, () => ({
      focus: () => {
        setIsFocused(true);
        focusableRef.current?.focus?.();

        setTimeout(() => {
          inputRef.current?.focus?.();
        }, 0);
      },
      blur: () => {
        setIsFocused(false);
        inputRef.current?.blur?.();
      },
      focusAccessibility: focusInputAccessibility,
      focusKeyboard: () => {
        setIsFocused(true);
        focusableRef.current?.focus?.();
      },
      focusAll: () => {
        setIsFocused(true);
        focusableRef.current?.focus?.();

        setTimeout(() => {
          inputRef.current?.focus?.();

          setTimeout(
            () => {
              focusInputAccessibility();
            },
            Platform.OS === 'android' ? 180 : 80,
          );
        }, 0);
      },
    }));

    useEffect(() => {
      if (isFocused) {
        return;
      }

      if (!isUS) {
        setLocalValue(value || '');
        return;
      }

      const rawDigits = normalizeDigits(value || '');

      if (isValidUSPhone(rawDigits)) {
        setLocalValue(formatUSPhone(normalizeUSPhoneForSave(rawDigits)));
      } else {
        setLocalValue(rawDigits);
      }
    }, [value, isFocused, isUS]);

    const getBlurValue = () => {
      if (isUS) {
        return normalizeDigits(localValue);
      }

      return localValue;
    };

    const handleInputFocus = () => {
      setIsFocused(true);

      if (isUS) {
        setLocalValue(normalizeDigits(localValue || value));
      }
    };

    const handleTextChange = (text: string) => {
      const cleaned = normalizeDigits(text);
      const nextValue = isUS ? cleaned.slice(0, 10) : cleaned;

      setLocalValue(nextValue);
      onChange(nextValue);
    };

    const handleInputBlur = () => {
      setIsFocused(false);

      const blurValue = getBlurValue();

      if (isUS) {
        if (isValidUSPhone(blurValue)) {
          setLocalValue(formatUSPhone(normalizeUSPhoneForSave(blurValue)));
        } else {
          setLocalValue(blurValue);
        }
      } else {
        setLocalValue(blurValue);
      }

      onChange(blurValue);
      onBlurValue?.(blurValue);
    };

    const handleSubmitEditing = () => {
      inputRef.current?.blur?.();
    };

    return (
      <View style={styles.container} accessible={false}>
        <View style={styles.row} accessible={false}>
          <View
            style={[
              styles.inputWrapper,
              isFocused && !hasError && styles.inputWrapperFocused,
              hasError && styles.inputWrapperError,
            ]}
            accessible={false}>
            <View style={styles.inputSlot} accessible={false}>
              <KeyboardInput
                key={`${testID || phoneInput.input}-${PHONE_PLACE_HOLDER}`}
                ref={inputRef}
                value={localValue}
                onChangeText={handleTextChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onSubmitEditing={handleSubmitEditing}
                returnKeyType="done"
                keyboardType={
                  Platform.OS === 'ios' ? 'number-pad' : 'phone-pad'
                }
                autoCorrect={false}
                spellCheck={false}
                placeholder={PHONE_PLACE_HOLDER}
                placeholderTextColor="#9CA3AF"
                underlineColorAndroid="transparent"
                selectionColor="#1E1E1E"
                style={styles.input}
                accessible
                accessibilityRole="text"
                accessibilityLabel={resolvedAccessibilityLabel}
                accessibilityHint={resolvedAccessibilityHint}
                accessibilityValue={accessibilityValue}
                accessibilityState={accessibilityState}
                testID={phoneInput.input}
                allowFontScaling
                maxFontSizeMultiplier={2}
              />
            </View>
          </View>

          {onDelete ? (
            <TouchableOpacity
              style={styles.deleteBtn}
              onPress={onDelete}
              testID={phoneInput.delete}
              accessible={!hideDeleteFromAccessibility}
              focusable={!hideDeleteFromAccessibility}
              importantForAccessibility={
                hideDeleteFromAccessibility ? 'no-hide-descendants' : 'yes'
              }
              accessibilityRole="button"
              accessibilityLabel="Delete phone number"
              accessibilityHint="Double tap to remove this phone number">
              <Icon name="trash" style={styles.trashIcon} accessible={false} />
            </TouchableOpacity>
          ) : null}
        </View>

        {hasError && errorText ? (
          <View
            testID={phoneInput.error}
            accessible
            accessibilityRole="alert"
            accessibilityLabel={`Error: ${errorText}`}>
            <Text
              style={styles.errorText}
              accessible={false}
              allowFontScaling
              maxFontSizeMultiplier={2}>
              {errorText}
            </Text>
          </View>
        ) : null}
      </View>
    );
  },
);

export default PhoneInput;
